<template>
  <div>
    <div>
      <div>
        <p
          v-if="error"
          class="text-danger"
        >
          {{ error }}
        </p>
        <table class="info-table">
          <tr>
            <td>Name</td>
            <td>{{ project.name }}</td>
            <td />
          </tr>
          <tr>
            <td>Delivery Group</td>
            <td>{{ project.project_type.name }}</td>
            <td />
          </tr>
          <tr>
            <td>{{ teamFieldName }}</td>
            <td>
              {{ project.team ? project.team : 'Unassigned' }}
            </td>
            <td>
              <span
                v-if="canEditTeam"
                class="edit-icon"
                :title="`Assign ${teamFieldName}`"
                @click="editTeam"
              >
                <EditIcon :title="`Assign ${teamFieldName}`" />
              </span>
            </td>
          </tr>
          <tr
            v-for="r in roles"
            :key="'role-' + r.usr_role.id"
          >
            <td>{{ r.usr_role.name }}</td>
            <td>
              <template v-if="r.usr">
                {{ r.usr.name }}
              </template>
              <template v-else>
                Unassigned
              </template>
            </td>
            <td>
              <span
                v-if="r.can_edit"
                :title="'Assign ' + r.usr_role.name"
                class="edit-icon"
                @click="editRole(r)"
              >
                <EditIcon :title="'Assign ' + r.usr_role.name" />
              </span>
            </td>
          </tr>
          <tr>
            <td>Status</td>
            <td>
              <span :class="{ 'text-danger': project.project_state.is_start }">
                {{ project.project_state.name }}
              </span>
            </td>
            <td>
              <span
                v-if="transitions.length > 0"
                class="edit-icon"
                title="Change State"
                @click="changeState"
              >
                <EditIcon title="Change State" />
              </span>
            </td>
          </tr>
          <tr v-if="project.sla_target">
            <td>SLA</td>
            <td>{{ formatSLA(project.sla_actual) }} / {{ formatSLA(project.sla_target) }}</td>
          </tr>
          <tr
            v-for="prop in properties"
            :key="prop.fullname"
          >
            <td>{{ prop.schema.title }}</td>
            <td>
              <ProjectInfoProperty
                :property="prop.base[prop.name]"
                :schema="prop.schema"
              />
            </td>
            <td>
              <span
                v-if="!prop.schema.readOnly"
                class="edit-icon"
                :title="`Edit ${prop.schema.title}`"
                @click="editProp(prop)"
              >
                <EditIcon :title="`Edit ${prop.schema.title}`" />
              </span>
            </td>
          </tr>
          <tr
            v-for="(at, typeName) in attachments"
            :key="typeName"
          >
            <td>{{ typeName }}</td>
            <td>
              <a
                v-for="a in at"
                :key="at.id + '_' + a.id"
                :href="downloadLink(a.id)"
                class="link-list"
              >{{ a.filename }}</a>
            </td>
            <td />
          </tr>
        </table>
      </div>
      <div
        v-if="canEdit || canDelete || canAttachFile || namedTransitions.length > 0"
        class="project-panel-footer btn-block"
      >
        <router-link
          v-if="canEdit"
          :to="{ name: 'update_project' }"
          class="btn btn-primary"
          tag="button"
        >
          Edit
        </router-link>
        <button
          v-if="canDelete"
          class="btn btn-primary"
          @click.prevent="confirmDelete()"
        >
          Delete
        </button>
        <button
          v-if="canAttachFile"
          class="btn btn-primary"
          @click="attachFile"
        >
          Add Attachment
        </button>
        <template v-for="tr in namedTransitions">
          <span :key="'span' + tr.dst_state.id" />
          <button
            :key="tr.dst_state.id"
            class="btn btn-primary"
            @click="changeStateID(tr)"
          >
            {{ tr.name }}
          </button>
        </template>
      </div>
    </div>
    <ProjectTeamUpdate
      ref="teamUpdate"
      @team-update="refreshData"
    />
    <ProjectRoleUpdate
      ref="roleUpdate"
      @role-update="refreshData"
    />
    <ProjectPropertyUpdate ref="propertyUpdate" />
    <ProjectP4PPropUpdate
      ref="p4pPropUpdate"
      :project="project"
      @prop-update="refreshData"
    />
    <ProjectStateUpdate
      ref="stateUpdate"
      @state-change="refreshData"
    />
    <AttachFile
      ref="attachFile"
      :project="project"
      @file-upload="refreshData"
    />
    <ProjectDeliveryDateUpdate ref="deliveryDateUpdate" />
    <ConfirmStateChange
      ref="confirmStateChange"
      @state-change="refreshData"
    />
    <ProjectSurvey
      ref="survey"
      :project="project"
    />
    <ConfirmDialog
      ref="dialog"
      @confirmed="deleteProject"
    />
  </div>
</template>

<script>
/**
 * View to Show Project Information.
 */
import EditIcon from 'vue-material-design-icons/SquareEditOutline.vue'
import projectAPI from '@/api/project'
import ProjectInfoProperty from './ProjectInfoProperty'
import ProjectTeamUpdate from './ProjectTeamUpdate'
import ProjectRoleUpdate from './ProjectRoleUpdate'
import ProjectPropertyUpdate from './ProjectPropertyUpdate'
import ProjectP4PPropUpdate from './ProjectP4PPropUpdate'
import ProjectStateUpdate from './ProjectStateUpdate'
import AttachFile from './AttachFile'
import ProjectSurvey from './ProjectSurvey'
import ProjectDeliveryDateUpdate from './ProjectDeliveryDateUpdate'
import ConfirmStateChange from './ConfirmStateChange'
import ConfirmDialog from '@/components/ConfirmDialog'
import formatDate from '@/filters/formatDate'

export default {
  components: {
    EditIcon,
    ProjectInfoProperty,
    ProjectTeamUpdate,
    ProjectRoleUpdate,
    ProjectPropertyUpdate,
    ProjectP4PPropUpdate,
    ProjectStateUpdate,
    AttachFile,
    ProjectSurvey,
    ProjectDeliveryDateUpdate,
    ConfirmDialog,
    ConfirmStateChange,
  },
  props: {
    // Project ID
    projectId: {
      type: [String, Number],
      required: true,
    },
  },
  data () {
    return {
      project: {
        name: '',
        creator: { name: '' },
        project_type: { name: '' },
        project_state: { name: '' },
        properties: {
          platforms: [],
          requesting_group: '',
          decommission: '',
          sites: [],
        },
      },
      roles: [],
      attachments: {},
      transitions: [],
      permissions: [],
      schema: { properties: {} },
      error: '',
    }
  },
  computed: {
    canEdit () {
      return this.canDo('PUT', 'project')
    },
    canEditTeam () {
      return this.canDo('PUT', 'team')
    },
    canEditDeliveryDate () {
      return this.canDo('PUT', 'delivery_date')
    },
    canEditPanCode () {
      return this.canDo('PUT', 'pan_code')
    },
    canAttachFile () {
      return this.canDo('POST', 'attachments')
    },
    canDelete () {
      return this.canDo('DELETE', 'project')
    },
    canCreateSurvey () {
      return this.canDo('POST', 'survey')
    },
    nextStates () {
      return this.transitions.map(tr => tr.dst_state)
    },
    namedTransitions () {
      return this.transitions.filter(tr => tr.name)
    },
    properties () {
      return this.getObjectProps(this.schema, this.project)
    },
    teamFieldName () {
      return process.env.VUE_APP_TEAM_NAME
    },
  },
  beforeMount () {
    this.getData()
  },
  methods: {
    getObjectProps (schema, data) {
      let props = schema['ui:order'] || []
      if (props.length === 0) {
        props = Object.keys(schema.properties || {}).sort()
      }
      return props.map(p => {
        const propSchema = schema.properties[p]
        if (propSchema.type === 'object') {
          // get nested props
          return this.getObjectProps(propSchema, data[p]).map(op => {
            return { ...op, fullname: p + '.' + op.name }
          })
        }
        return {
          name: p,
          schema: propSchema,
          base: data,
          fullname: name,
          required: schema.required && schema.required.includes(p),
        }
      }).flat()
    },
    getDataPromise () {
      this.error = ''
      return Promise.all([
        projectAPI.getProject(this.projectId),
        projectAPI.getProjectRoles(this.projectId),
        projectAPI.getProjectRolePermissions(this.projectId),
        projectAPI.getProjectTransitions(this.projectId),
        projectAPI.getProjectPermissions(this.projectId),
        projectAPI.getAttachments(this.projectId),
        projectAPI.getSchema(this.projectId),
      ]).then(([project, roles, perms, transitions, permissions, attachments, schema]) => {
        project = project.data
        this.roles = roles.data
        this.roles.forEach(role => {
          const perm = perms.data.find(p => p.usr_role_id === role.usr_role.id)
          if (perm) { role.can_edit = perm.can_edit }
        })
        // group attachments by type name
        this.attachments = attachments.data.reduce(function (atlist, at) {
          if (!atlist[at.type_name]) {
            atlist[at.type_name] = [at]
          } else {
            // filter out older files with same name and type
            atlist[at.type_name] = atlist[at.type_name].filter(a => a.filename !== at.filename)
            atlist[at.type_name].push(at)
          }
          return atlist
        }, {})
        this.transitions = transitions.data
        this.permissions = permissions.data
        this.schema = schema.data
        this.project = project
        if (this.canCreateSurvey) {
          this.$refs.survey.start(project)
        }
      })
    },
    getData () {
      this.getDataPromise().catch(error => {
        this.error = error.message
      })
    },
    refreshData () {
      // Get parent Project view to reload project data.
      // TODO: optimize better to remove duplication of requests.
      this.$emit('reload-project')

      this.getDataPromise().catch(error => {
        if (error.request.status === 403) {
          // status change changed project viewing permissions so go back
          this.$router.go(-1)
        } else {
          this.error = error.message
        }
      })
    },
    downloadLink (id) {
      return process.env.VUE_APP_API_URL + projectAPI.attachmentDownloadURL(this.projectId, id)
    },
    editTeam () {
      this.$refs.teamUpdate.edit(this.project)
    },
    editProp (prop) {
      this.$refs.propertyUpdate.edit(this.project, prop)
    },
    editRole (role) {
      this.$refs.roleUpdate.edit(this.project, role)
    },
    changeState () {
      this.$refs.stateUpdate.edit(this.project, this.nextStates)
    },
    editDeliveryDate () {
      this.$refs.deliveryDateUpdate.edit(this.project)
    },
    editPanCode () {
      const field = {
        name: 'pan_code',
        required: true,
        schema: {
          type: 'string',
          title: 'Pan Code',
        },
      }
      this.$refs.p4pPropUpdate.edit(field)
    },
    attachFile (typeID) {
      this.$refs.attachFile.show(typeID)
    },
    changeStateID (transition) {
      this.$refs.confirmStateChange.toConfirm(transition, this.projectId)
    },
    confirmDelete () {
      this.error = ''
      this.$refs.dialog.confirm(
        'Confirm Delete',
        'Are you sure you want to delete the current Ticket?',
      )
    },
    deleteProject () {
      projectAPI.deleteProject(this.projectId).then(resp => {
        this.$router.push({ name: 'projects' })
      }).catch(error => {
        this.error = error.message
      })
    },
    canDo (method, resource) {
      return this.permissions.findIndex(p => (p.method === method) && (p.resource === resource)) >= 0
    },
    formatSLA (sla) {
      const mins = sla % 60
      const hours = Math.floor(sla / 60) % 24
      const days = Math.floor(sla / (60 * 24))
      const parts = []
      if (days === 1) {
        parts.push(`${days} day`)
      } else if (days > 0) {
        parts.push(`${days} days`)
      }
      if (hours === 1) {
        parts.push(`${hours} hour`)
      } else if (hours > 0) {
        parts.push(`${hours} hours`)
      }
      if (mins === 1) {
        parts.push(`${mins} minute`)
      } else if (mins > 0 || parts.length === 0) {
        parts.push(`${mins} minutes`)
      }
      return parts.join(' ')
    },
    formatDate,
  },
}
</script>

<style scoped>
/* comma separated list */
.link-list:after {
  content: ",\00a0";
  display: inline-block;
  text-decoration: underline;
}
.link-list:last-child::after
{
  content: "";
}
/* for links don't show underline on :after part
   bug in IE requires underline be set, then unset for it to work */
.link-list:after, .link-list:hover:after {
  text-decoration: none;
}
.project-panel-footer {
  background-color: #f5f5f5;
  padding: 10px 15px;
  margin-top: 15px;
}
/* make column with edit icon minimum width */
td:nth-child(3) {
  width: 1%;
}
</style>
